import type { Logger } from "@climatepartner/app-shell/monitoring";
import { Box, Link, Typography } from "@mui/material";
import { Component, ComponentType, ErrorInfo, ReactElement, ReactNode } from "react";

import { CCF_CALCULATION_LOCAL_STORAGE_PREFIX } from "../calculations/calculation-storage-key";
import { SUPPORT_EMAIL } from "../support/support-email";

type ErrorBoundaryFallbackProps = {
  message?: string;
  error?: Error;
};

function ErrorBoundaryFallback({ message, error }: ErrorBoundaryFallbackProps): ReactElement {
  const errorMessage = message ?? (
    <>
      Sorry, something went wrong. Please try again later or contact{" "}
      <Link href={`mailto:${SUPPORT_EMAIL}`} target="_blank">
        {SUPPORT_EMAIL}
      </Link>
    </>
  );

  return (
    <Box>
      <Typography variant="h4" component="p">
        Something went wrong
      </Typography>

      <Typography component="p">{errorMessage}</Typography>

      {import.meta.env.MODE !== "production" && <h4>{error && <pre color="red">{error.stack}</pre>}</h4>}
    </Box>
  );
}

type ErrorBoundaryProps = {
  children: ReactNode;
  logger: Logger;
  FallbackComponent?: ComponentType;
};

type State = {
  error?: Error;
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  state = {
    error: undefined,
  };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  private clearCcfLocalStorage(): void {
    if (typeof localStorage !== "undefined") {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key !== null && key.startsWith(CCF_CALCULATION_LOCAL_STORAGE_PREFIX)) {
          localStorage.removeItem(key);
        }
      }
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.props.logger.error(error.message);
    this.props.logger.error(JSON.stringify(errorInfo));
  }

  render(): ReactNode {
    const { error } = this.state;
    const { children, FallbackComponent = ErrorBoundaryFallback } = this.props;
    if (error) {
      this.clearCcfLocalStorage();
      return <FallbackComponent error={error} />;
    }

    return children;
  }
}
